import React from 'react';

import { Box, Button, Divider, Icon } from '@rbilabs/universal-components';
import { Typography } from 'ucl/components/text/Typography';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { CopyButton, DebugToolModal, PluInfo, useDebugToolPosVendor } from 'components/debug-tool';
import { MenuObjectTypes } from 'enums/menu';
import { CartEntryType, ComboSlotUiPattern } from 'utils/cart/types';
import { findVendorConfig } from 'utils/vendor-config';

import { SelectedItemDebug } from '../components/selected-item-debug';
import { getSanityDocumentUrl } from '../utils';

const getCartEntryAsMenuObjectType = (cartEntry: ILegacyCartEntry) => {
  return cartEntry.type === CartEntryType.combo ? MenuObjectTypes.COMBO : MenuObjectTypes.ITEM;
};

const SelectedCartEntryComboSlotsDebug = ({
  cartEntry,
  posVendor,
}: {
  cartEntry: ILegacyCartEntry;
  posVendor: string;
}) => {
  const comboSlots = cartEntry.children
    .filter(
      (child: ILegacyCartEntry) =>
        child.type === CartEntryType.comboSlot && child.uiPattern !== ComboSlotUiPattern.HIDDEN
    )
    .flatMap((child: ILegacyCartEntry) => {
      return child.type === CartEntryType.item ? (
        <React.Fragment key={child._id}>
          <Divider />
          <SelectedItemDebug
            selectedItemVendorConfig={child}
            productName={child.name}
            posVendor={posVendor}
            selectedItemType={getCartEntryAsMenuObjectType(child)}
            hideTitle
            hidePosVendor
          />
        </React.Fragment>
      ) : (
        child.children.map((child: ILegacyCartEntry) => {
          const selectedEntryType = getCartEntryAsMenuObjectType(child);
          const vendorConfig = findVendorConfig(child.vendorConfigs, posVendor);

          return child ? (
            <React.Fragment key={child._id}>
              <Divider />
              <CopyButton
                label={child.name ?? ''}
                textToCopy={getSanityDocumentUrl(selectedEntryType, child._id)}
                variant="headerTwo"
                iconName="mi:content-copy"
              />
              {vendorConfig && <PluInfo vendorConfig={vendorConfig} />}
            </React.Fragment>
          ) : null;
        })
      );
    });

  return (
    <Box marginTop="$4">
      <Typography variant="headerOne">Combo Slots:</Typography>
      {comboSlots}
    </Box>
  );
};

const CartDebugModal: React.FC<{
  cartEntry: ILegacyCartEntry;
  onClose: () => void;
}> = ({ cartEntry, onClose }) => {
  const { storePosVendor } = useDebugToolPosVendor();
  const isCombo =
    cartEntry.type === CartEntryType.combo || cartEntry.type === CartEntryType.offerCombo;

  return (
    <DebugToolModal onOpenEventMessage="Menu Debug Tool: Cart Item" onClose={onClose}>
      <SelectedItemDebug
        selectedItemVendorConfig={cartEntry}
        productName={cartEntry.name}
        posVendor={storePosVendor}
        selectedItemType={getCartEntryAsMenuObjectType(cartEntry)}
      />
      <Divider />
      {isCombo && (
        <SelectedCartEntryComboSlotsDebug cartEntry={cartEntry} posVendor={storePosVendor} />
      )}
    </DebugToolModal>
  );
};

export const CartDebugMenu = ({ cartEntry }: { cartEntry?: ILegacyCartEntry }) => {
  const [isDebugMenuModalOpen, setIsDebugMenuModalOpen] = React.useState(false);

  return (
    <>
      <Button
        variant="ghost"
        onPress={() => setIsDebugMenuModalOpen(true)}
        leftIcon={<Icon name="mi:bug-report" />}
      >
        <Typography>View Product info</Typography>
      </Button>

      {cartEntry && isDebugMenuModalOpen ? (
        <CartDebugModal onClose={() => setIsDebugMenuModalOpen(false)} cartEntry={cartEntry} />
      ) : null}
    </>
  );
};
